import type {
    PayloadFieldConfig,
} from "@mrflark/core/hooks";
import type {
    ComponentChildren,
} from "preact";
import {
    useState,
} from "preact/hooks";
import {
    Button,
    Input,
    Modal,
} from "../mod.tsx";

type RunJobModalProps = {
    isOpen: boolean;
    jobType: string;
    jobTypeLabel: string;
    payloadFields: PayloadFieldConfig[];
    onConfirm: (jobType: string, payload: Record<string, unknown>) => void;
    onClose: () => void;
};

export const RunJobModal = ({
    isOpen,
    jobType,
    jobTypeLabel,
    payloadFields,
    onConfirm,
    onClose,
}: RunJobModalProps): ComponentChildren => {
    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const handleClose = (): void => {
        setValues({});
        setErrors({});
        onClose();
    };

    const handleSubmit = (): void => {
        const newErrors: Record<string, boolean> = {};
        for (const field of payloadFields) {
            if (field.required && !values[field.key]?.trim()) {
                newErrors[field.key] = true;
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const payload: Record<string, unknown> = {};
        for (const field of payloadFields) {
            const rawValue = values[field.key]?.trim();
            if (!rawValue) continue;
            payload[field.key] = field.type === "number"
                ? Number(rawValue)
                : rawValue;
        }

        onConfirm(jobType, payload);
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Run ${jobTypeLabel}`}
            size="sm"
        >
            <div className="space-y-4">
                {payloadFields.map((field) => (
                    <div key={field.key}>
                        <label className="block text-sm text-text-secondary mb-1">
                            {field.label}
                            {field.required && (
                                <span className="text-red-500 ml-1">*</span>
                            )}
                        </label>
                        <Input
                            type={field.type === "number" ? "number" : "text"}
                            placeholder={field.placeholder}
                            value={values[field.key] ?? ""}
                            onChange={(event) => {
                                const target = event.target as HTMLInputElement;
                                setValues((prev) => ({
                                    ...prev,
                                    [field.key]: target.value,
                                }));
                                setErrors((prev) => ({
                                    ...prev,
                                    [field.key]: false,
                                }));
                            }}
                            className={`w-full ${
                                errors[field.key] ? "border-red-500" : ""
                            }`}
                        />
                        {errors[field.key] && (
                            <p className="text-xs text-red-500 mt-1">
                                {field.label} is required
                            </p>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    Run Job
                </Button>
            </div>
        </Modal>
    );
};
